import React from 'react';
import { Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, InputLabel, LinearProgress, List, ListItem, ListItemText, MenuItem, Select, Stack, Typography } from '@mui/material';

interface Props {
  open: boolean;
  onClose: () => void;
  onGenerated?: (items: any[]) => void;
}

// UI-only dialog for generating packages from quantity takeoffs (przedmiary)
export default function AIGeneratePackagesDialog({ open, onClose, onGenerated }: Props) {
  const [provider, setProvider] = React.useState<string>(() => localStorage.getItem('ai:provider') || 'deepseek');
  const [model, setModel] = React.useState<string>(() => localStorage.getItem(`ai:model:${localStorage.getItem('ai:provider') || 'deepseek'}`) || 'deepseek-chat');
  const [loading, setLoading] = React.useState<boolean>(false);
  const [items, setItems] = React.useState<any[] | null>(null);

  React.useEffect(() => {
    if (!open) return;
    try {
      const p = localStorage.getItem('ai:provider') || 'deepseek';
      const m = localStorage.getItem(`ai:model:${p}`) || (p==='deepseek' ? 'deepseek-chat' : 'claude-3-5-sonnet-latest');
      setProvider(p);
      setModel(m);
    } catch {}
  }, [open]);

  const pickAndGenerate = () => {
    const picker = document.createElement('input');
    picker.type = 'file';
    picker.accept = '.pdf,.docx,.txt,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain';
    picker.multiple = true;
    picker.onchange = async () => {
      if (!picker.files || picker.files.length === 0) return;
      setLoading(true);
      try {
        try { window.dispatchEvent(new CustomEvent('ai:busy', { detail: { busy: true } })); } catch {}
        const form = new FormData();
        Array.from(picker.files).forEach(f => form.append('files', f));
        // Backend pobiera domyślną instrukcję z SQL (packages_packaging).
        // Dla kompatybilności – jeśli istnieje lokalny prompt, pozwól nadpisać.
        try {
          const saved = localStorage.getItem('ai:instr:packaging');
          if (saved && saved.trim()) {
            form.append('prompt', saved);
          }
        } catch {}
        const headers: Record<string,string> = { 'x-ai-provider': provider, 'x-ai-model': model } as any;
        const apiKey = localStorage.getItem(`ai:apiKey:${provider}`) || '';
        if (apiKey) (headers as any)['x-ai-api-key'] = apiKey;
        const { default: api } = await import('../../services/api');
        const resp = await api.post('/ai/packages/extract-json', form, { headers });
        const arr = resp.data?.data?.items || [];
        setItems(arr);
      } catch (e) {
        console.error('AI extract packages error', e);
        alert('Nie udało się wygenerować JSON pakietów z dokumentów.');
      } finally {
        setLoading(false);
        try { window.dispatchEvent(new CustomEvent('ai:busy', { detail: { busy: false } })); } catch {}
      }
    };
    picker.click();
  };

  const downloadJson = () => {
    if (!items) return;
    try {
      const blob = new Blob([JSON.stringify(items, null, 2)], { type: 'application/json;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'packages_ai_generated.json';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {}
  };

  const handleImport = () => {
    if (!items) return;
    if (onGenerated) {
      try { onGenerated(items); } catch {}
    }
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box sx={{ mr: 1 }}>Generuj pakiety z przedmiarów</Box>
        {loading && (
          <Box sx={{ display: 'flex', alignItems: 'center', bgcolor: '#6b46c1', color: '#fff', px: 1.25, py: 0.5, borderRadius: 99, fontSize: 13, fontWeight: 700, boxShadow: '0 2px 8px rgba(0,0,0,0.15)' }}>
            <Box component="img" src="/assets/ai_badge_unitresco.svg" alt="AI" sx={{ width: 16, height: 16, mr: 1 }} />
            Generuję propozycje…
          </Box>
        )}
      </DialogTitle>
      <DialogContent dividers>
        <Stack direction={{ xs:'column', sm:'row' }} spacing={1} sx={{ mb: 2 }} alignItems={{ sm:'center' }}>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel id="ai-prov">Dostawca</InputLabel>
            <Select labelId="ai-prov" label="Dostawca" value={provider} onChange={(e)=>{
              const p = String(e.target.value);
              setProvider(p);
              const m = localStorage.getItem(`ai:model:${p}`) || (p==='deepseek' ? 'deepseek-chat' : 'claude-3-5-sonnet-latest');
              setModel(m);
            }}>
              <MenuItem value="deepseek">DeepSeek</MenuItem>
              <MenuItem value="anthropic">Anthropic</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel id="ai-model">Model</InputLabel>
            <Select labelId="ai-model" label="Model" value={model} onChange={(e)=>setModel(String(e.target.value))}>
              {provider==='deepseek' ? (
                [
                  <MenuItem key="deepseek-chat" value="deepseek-chat">deepseek-chat</MenuItem>,
                  <MenuItem key="deepseek-reasoner" value="deepseek-reasoner">deepseek-reasoner</MenuItem>
                ]
              ) : (
                [
                  <MenuItem key="claude-3-5-sonnet-latest" value="claude-3-5-sonnet-latest">claude-3.5-sonnet</MenuItem>,
                  <MenuItem key="claude-3-sonnet-20240229" value="claude-3-sonnet-20240229">claude-3-sonnet-20240229</MenuItem>
                ]
              )}
            </Select>
          </FormControl>
          <Button
            variant="outlined"
            startIcon={<img src="/assets/ai_badge_unitresco.svg" alt="AI" width={22} height={22} style={{ display: 'block' }} />}
            onClick={pickAndGenerate}
            disabled={loading}
          >
            Wybierz pliki i generuj
          </Button>
          <Button variant="outlined" onClick={downloadJson} disabled={!items || loading}>Pobierz JSON (AI)</Button>
          {loading && <LinearProgress sx={{ flex: 1 }} />}
        </Stack>

        {Array.isArray(items) && items.length > 0 && (
          <List dense>
            {items.map((it, idx) => (
              <ListItem key={idx}>
                <ListItemText
                  primary={<>
                    <Typography variant="subtitle2">{(it.package_symbol || '-')}: {(it.name || '-')}</Typography>
                  </>}
                  secondary={<>
                    <Typography variant="caption" color="text.secondary">
                      Szacowana wartość: {typeof it.estimated_value === 'number' ? `${it.estimated_value.toLocaleString('pl-PL')} PLN` : '-'}
                    </Typography>
                  </>}
                />
                <Chip size="small" color="success" label="OK" />
              </ListItem>
            ))}
          </List>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Zamknij</Button>
        <Button variant="contained" onClick={handleImport} disabled={!items}>Importuj</Button>
      </DialogActions>
    </Dialog>
  );
}


